import { Clock, Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Dom from "foldkit/dom";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import { listApiKeys } from "../../api/commands/integrations";
import { getRecord } from "../../api/commands/records";
import {
	TaskSection,
	addTaskComment,
	demoteTaskCanonical,
	getTask,
	getTaskHistory,
	getTaskScorecard,
	getTaskTree,
	listScorecardConfigs,
	listTaskComments,
	promoteTaskComment,
	runTaskScorecard,
} from "../../api/commands/tasks";
import type { ApiError } from "../../api/errors";
import { CommentType } from "../../api/schemas/tasks";
import type { Route } from "../../app/routes";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { COMPOSER_BODY_ID, type Loaded, type Model } from "./model";

/**
 * Task: one task's read view (sections, discussion, subtasks, attributes,
 * activity and scorecard).
 *
 * KTD8: tasks are not time-versioned, so the fetch is live and tagged by
 * generation and task id only; a result for an older generation or another
 * task is dropped. While `Shared.asOf` is set the view shows a one-line
 * "live data" notice and offers no writes. A new route id reloads; a new
 * asOf changes nothing.
 *
 * One Command loads everything: the task, its comments, tree, history,
 * scorecard runs, the scorecard configs (criterion labels) and api keys
 * (agent names), then the parent title, each subtask's latest grade and the
 * people's names. Only the task is required; see `Loaded`.
 *
 * Writes (comment, reply, promote, demote, re-score) go one at a time and
 * refetch the whole page on success: the server is the source of truth for
 * sections. None of these endpoints takes `ifLastUpdated` (only PATCH does);
 * a promote/demote that loses the server's compare-and-swap answers 409,
 * which refetches too and says the task changed. A failed comment keeps
 * the draft. Under asOf every write is hidden and a stray message is a no-op.
 */

const orNull = Result.getOrNull;

/** Reply moves focus to the composer (which may sit far below the section); a missing composer is not an error. */
export const FocusComposer = Command.define("FocusTaskComposer", {
	messages: [Message.CompletedFocusComposer],
	execute: Dom.focus(`#${COMPOSER_BODY_ID}`).pipe(
		Effect.as(Message.CompletedFocusComposer()),
		Effect.catch(() => Effect.succeed(Message.CompletedFocusComposer())),
	),
});

export const FetchTask = Command.define("FetchTask", {
	args: { id: S.String, generation: S.Number },
	messages: [Message.CompletedFetchTask],
	execute: ({ id, generation }) =>
		Effect.gen(function* () {
			const now = yield* Clock.currentTimeMillis;
			const [detail, comments, tree, history, scorecard, configs, keys] =
				yield* Effect.all(
					[
						getTask.run({ id }),
						listTaskComments.run({ id }),
						getTaskTree.run({ id }),
						getTaskHistory.run({ id }),
						getTaskScorecard.run({ id }),
						listScorecardConfigs.run({}),
						listApiKeys.run({}),
					],
					{ concurrency: "unbounded" },
				);
			const done = (result: Result.Result<Loaded, ApiError>) =>
				Message.CompletedFetchTask({ id, generation, now, result });
			if (Result.isFailure(detail)) return done(Result.fail(detail.failure));
			const task = detail.success.task;
			const descendants =
				orNull(tree)?.tasks.filter((t) => t.id !== task.id) ?? null;
			const [parent, grades, people] = yield* Effect.all(
				[
					task.parent_task_id === null
						? Effect.succeed(null)
						: getTask
								.run({ id: task.parent_task_id })
								.pipe(Effect.map((r) => orNull(r)?.task ?? null)),
					// ponytail: one scorecard call per subtask; a grade on /tree if trees grow large.
					Effect.forEach(
						descendants ?? [],
						(t) =>
							getTaskScorecard
								.run({ id: t.id })
								.pipe(
									Effect.map(
										(r) => orNull(r)?.results[0]?.overall_grade ?? null,
									),
								),
						{ concurrency: 4 },
					),
					Effect.forEach(
						task.people,
						(nodeId) =>
							getRecord
								.run({ nodeId, asOf: null })
								.pipe(
									Effect.map((r) => [nodeId, orNull(r)?.name ?? null] as const),
								),
						{ concurrency: 4 },
					),
				],
				{ concurrency: "unbounded" },
			);
			return done(
				Result.succeed({
					task,
					parent: parent && { id: parent.id, title: parent.title },
					comments: orNull(comments)?.comments ?? null,
					subtasks:
						descendants?.map((t, i) => ({
							task: t,
							grade: grades[i] ?? null,
						})) ?? null,
					history: orNull(history)?.data ?? null,
					scorecard: orNull(scorecard)?.results ?? null,
					configs: orNull(configs)?.configs ?? [],
					keys: orNull(keys)?.data ?? [],
					people: Object.fromEntries(
						people.flatMap(([rid, name]) =>
							name === null ? [] : [[rid, name]],
						),
					),
				}),
			);
		}),
});

export const RunScorecard = Command.define("RunTaskScorecard", {
	args: { id: S.String },
	messages: [Message.CompletedRescore],
	execute: ({ id }) =>
		runTaskScorecard
			.run({ id })
			.pipe(Effect.map((result) => Message.CompletedRescore({ id, result }))),
});

const writeDone =
	(id: string, kind: "comment" | "section") =>
	(result: Result.Result<unknown, ApiError>) =>
		Message.CompletedWrite({ id, kind, result });

export const AddComment = Command.define("AddTaskComment", {
	args: {
		id: S.String,
		body: S.String,
		type: CommentType,
		parentCommentId: S.NullOr(S.String),
	},
	messages: [Message.CompletedWrite],
	execute: (args) =>
		addTaskComment.run(args).pipe(Effect.map(writeDone(args.id, "comment"))),
});

export const PromoteComment = Command.define("PromoteTaskComment", {
	args: { id: S.String, commentId: S.String },
	messages: [Message.CompletedWrite],
	execute: ({ id, commentId }) =>
		promoteTaskComment
			.run({ commentId })
			.pipe(Effect.map(writeDone(id, "section"))),
});

export const DemoteCanonical = Command.define("DemoteTaskCanonical", {
	args: { id: S.String, section: TaskSection, commentId: S.String },
	messages: [Message.CompletedWrite],
	execute: (args) =>
		demoteTaskCanonical
			.run(args)
			.pipe(Effect.map(writeDone(args.id, "section"))),
});

const emptyDraft: Model["draft"] = {
	body: "",
	type: "discussion",
	replyTo: null,
};

export const blank = (id: string): Model => ({
	id,
	detail: AsyncData.Idle(),
	now: 0,
	rescore: "idle",
	draft: emptyDraft,
	saving: false,
	writeError: null,
	writeAt: "",
});

/** Which alert a failed re-score shows. */
const rescoreFailure = (error: ApiError): Model["rescore"] =>
	error._tag === "AiDisabled"
		? "aiDisabled"
		: error._tag !== "Http"
			? "failed"
			: error.status === 502
				? "upstream"
				: /not configured/i.test(error.message)
					? "aiNotConfigured"
					: "failed";

const isConflict = (error: ApiError) =>
	error._tag === "Http" && error.status === 409;

type Return = PageReturn<Model, Message>;

const load = (id: string, shared: Shared): Return => ({
	model: evo(blank(id), { detail: start }),
	commands: [FetchTask({ id, generation: shared.generation })],
});

const refetch = (model: Model, shared: Shared) =>
	FetchTask({ id: model.id, generation: shared.generation });

/** A write may start: live data, nothing else in flight. */
const canWrite = (model: Model, shared: Shared) =>
	Option.isNone(shared.asOf) && !model.saving;

/** A new write clears both the last write's alert and a stale re-score alert. */
const writing = (
	model: Model,
	command: NonNullable<Return["commands"]>[number],
	writeAt: string,
): Return => ({
	model: evo(model, {
		saving: () => true,
		writeError: () => null,
		writeAt: () => writeAt,
		rescore: (r) => (r === "running" ? r : "idle"),
	}),
	commands: [command],
});

const routeId = (route: Route) => (route._tag === "Task" ? route.id : "");

export const page: Page<Model, Message> = {
	init: (route, shared) => load(routeId(route), shared),
	// A new id is a different task: start over. A new asOf changes nothing (KTD8).
	changedRoute: (model, _previous, next, shared) =>
		routeId(next) === model.id ? { model } : load(routeId(next), shared),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchTask: ({ id, generation, now, result }) =>
				id !== model.id || generation !== shared.generation
					? { model }
					: {
							model: evo(model, {
								detail: (detail) => settle(detail, result),
								now: () => now,
							}),
						},
			// Writes are hidden under asOf; a stray click there does nothing.
			ClickedRescore: () =>
				Option.isSome(shared.asOf) || model.rescore === "running"
					? { model }
					: {
							model: evo(model, {
								rescore: () => "running" as const,
								writeError: () => null,
							}),
							commands: [RunScorecard({ id: model.id })],
						},
			// A new run is a new scorecard row: refetch, keeping the page on screen.
			CompletedRescore: ({ id, result }) =>
				id !== model.id
					? { model }
					: Result.isFailure(result)
						? {
								model: evo(model, {
									rescore: () => rescoreFailure(result.failure),
								}),
							}
						: {
								model: evo(model, {
									rescore: () => "idle" as const,
									detail: start,
								}),
								commands: [
									FetchTask({ id: model.id, generation: shared.generation }),
								],
							},
			ChangedDraft: ({ body }) => ({
				model: evo(model, { draft: (d) => ({ ...d, body }) }),
			}),
			ChangedDraftType: ({ type }) => ({
				model: evo(model, { draft: (d) => ({ ...d, type }) }),
			}),
			ClickedReply: ({ commentId, type }) => ({
				model: evo(model, {
					draft: (d) => ({ ...d, type, replyTo: commentId }),
				}),
				commands: [FocusComposer()],
			}),
			CompletedFocusComposer: () => ({ model }),
			ClickedCancelReply: () => ({
				model: evo(model, { draft: (d) => ({ ...d, replyTo: null }) }),
			}),
			SubmittedComment: () =>
				!canWrite(model, shared) || model.draft.body.trim() === ""
					? { model }
					: writing(
							model,
							AddComment({
								id: model.id,
								body: model.draft.body.trim(),
								type: model.draft.type,
								parentCommentId: model.draft.replyTo,
							}),
							"composer",
						),
			ClickedPromote: ({ commentId }) =>
				canWrite(model, shared)
					? writing(
							model,
							PromoteComment({ id: model.id, commentId }),
							commentId,
						)
					: { model },
			ClickedDemote: ({ section, commentId }) =>
				canWrite(model, shared)
					? writing(
							model,
							DemoteCanonical({ id: model.id, section, commentId }),
							commentId,
						)
					: { model },
			CompletedWrite: ({ id, kind, result }) => {
				if (id !== model.id) return { model };
				const failure = Result.isFailure(result) ? result.failure : null;
				const settled = evo(model, {
					saving: () => false,
					writeError: () =>
						failure === null
							? null
							: isConflict(failure)
								? ("conflict" as const)
								: ("failed" as const),
					// A posted comment clears the composer; a failed one keeps the text.
					draft: (d) =>
						failure === null && kind === "comment"
							? { ...emptyDraft, type: d.type }
							: d,
				});
				return failure !== null && !isConflict(failure)
					? { model: settled }
					: {
							model: evo(settled, { detail: start }),
							commands: [refetch(model, shared)],
						};
			},
		}),
};
